import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { ShoppingCart, Filter } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { formatINR } from '../utils/currency';

const BACKEND_URL = (typeof window !== 'undefined' && window.location.origin) || process.env.REACT_APP_BACKEND_URL;

const Products = () => {
  const [searchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState(() => searchParams.get('category') || 'all');
  const navigate = useNavigate();
  const { user } = useAuth();

  const categories = [
    { id: 'all', name: 'All Products' },
    { id: 'skincare', name: 'Skincare' },
    { id: 'apparel', name: 'Art & Apparel' },
    { id: 'furniture', name: 'Furniture by MMC' },
    { id: 'health-fitness', name: 'Health & Fitness' },
    { id: 'featured-brands', name: 'Featured Brands' }
  ];

  const fetchProducts = useCallback(async () => {
    try {
      setLoading(true);
      const url = selectedCategory === 'all' 
        ? `${BACKEND_URL}/api/products`
        : `${BACKEND_URL}/api/products?category=${selectedCategory}`;
      
      const { data } = await axios.get(url);
      setProducts(data);
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') {
        console.error('Error fetching products:', error);
      }
    } finally {
      setLoading(false);
    }
  }, [selectedCategory]);

  useEffect(() => {
    const category = searchParams.get('category');
    if (category && category !== selectedCategory) {
      setSelectedCategory(category);
    }
  }, [searchParams, selectedCategory]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const addToCart = async (productId) => {
    if (!user) {
      navigate('/login');
      return;
    }

    try {
      await axios.post(
        `${BACKEND_URL}/api/cart/add`,
        { product_id: productId, quantity: 1 },
        { withCredentials: true }
      );
      alert('Added to cart!');
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') {
        console.error('Error adding to cart:', error);
      }
      
      // Check if it's an auth error
      if (error.response?.status === 401) {
        alert('Session expired. Please login again.');
        navigate('/login');
      } else if (error.response?.status === 400) {
        alert(error.response?.data?.detail || 'Product not available');
      } else {
        alert('Failed to add to cart. Please try again.');
      }
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold mb-4">Our Products</h1>
          <p className="text-gray-600">Discover our curated collection of women-led products</p>
        </div>

        {/* Category Filter */}
        <div className="mb-8 flex items-center justify-center space-x-2 flex-wrap gap-2">
          <Filter className="w-5 h-5 text-gray-600" />
          {categories.map((cat) => (
            <button
              key={cat.id}
              onClick={() => setSelectedCategory(cat.id)}
              className={`px-4 py-2 rounded-lg font-medium transition ${
                selectedCategory === cat.id
                  ? 'bg-purple-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-100'
              }`}
              data-testid={`category-filter-${cat.id}`}
            >
              {cat.name}
            </button>
          ))}
        </div>

        {/* Products Grid */}
        {loading ? (
          <div className="flex justify-center items-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
          </div>
        ) : products.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-gray-600 text-lg">No products found in this category.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
            {products.map((product) => (
              <div
                key={product.id}
                className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-xl transition"
                data-testid={`product-card-${product.id}`}
              >
                <div
                  className="h-64 bg-gray-200 cursor-pointer"
                  onClick={() => navigate(`/product/${product.id}`)}
                >
                  {product.images && product.images.length > 0 ? (
                    <img
                      src={product.images[0]}
                      alt={product.name}
                      className="w-full h-full object-cover hover:scale-105 transition duration-300"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-400">
                      No Image
                    </div>
                  )}
                </div>
                <div className="p-4">
                  <h3 className="text-lg font-semibold mb-2 line-clamp-2">{product.name}</h3>
                  <p className="text-gray-600 text-sm mb-3 line-clamp-2">{product.description}</p>
                  
                  {/* Nuskay Website Link for Skincare Products */}
                  {product.category === 'skincare' && (
                    <a
                      href="https://www.nuskay.in"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center text-sm text-purple-600 hover:text-purple-700 font-medium mb-3 transition group/link"
                    >
                      <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                      </svg>
                      <span className="border-b border-purple-600 group-hover/link:border-purple-700">Visit Nuskay Skincare</span>
                      <svg className="w-3 h-3 ml-1 group-hover/link:translate-x-0.5 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </a>
                  )}

                  {/* Nanaki Kaur Website Link for Art Tee Products */}
                  {product.name && product.name.includes('Art Tee') && (
                    <a
                      href="https://www.sabaariyakaur.com"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center text-sm text-purple-600 hover:text-purple-700 font-medium mb-3 transition group/link"
                    >
                      <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                      </svg>
                      <span className="border-b border-purple-600 group-hover/link:border-purple-700">Visit Nanaki Kaur</span>
                      <svg className="w-3 h-3 ml-1 group-hover/link:translate-x-0.5 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </a>
                  )}

                  {/* MMC Wood Creations link for Furniture products */}
                  {product.category === 'furniture' && (
                    <a
                      href={product.external_url || 'https://mmcwoodcreations.com'}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center text-sm text-amber-700 hover:text-amber-800 font-medium mb-3 transition group/link"
                      data-testid={`mmc-link-${product.id}`}
                    >
                      <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                      </svg>
                      <span className="border-b border-amber-700 group-hover/link:border-amber-800">View on MMC Wood Creations</span>
                      <svg className="w-3 h-3 ml-1 group-hover/link:translate-x-0.5 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </a>
                  )}

                  {/* Overcmr link for Health & Fitness products */}
                  {product.category === 'health-fitness' && product.external_url && (
                    <a
                      href={product.external_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center text-sm text-emerald-700 hover:text-emerald-800 font-medium mb-3 transition group/link"
                      data-testid={`overcmr-link-${product.id}`}
                    >
                      <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                      </svg>
                      <span className="border-b border-emerald-700 group-hover/link:border-emerald-800">View on Overcmr</span>
                      <svg className="w-3 h-3 ml-1 group-hover/link:translate-x-0.5 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </a>
                  )}

                  {/* Featured Brand external link */}
                  {product.category === 'featured-brands' && (
                    <div className="mb-3">
                      {product.brand_name && (
                        <p className="text-xs uppercase tracking-wide text-pink-600 font-semibold mb-1">
                          by {product.brand_name}
                        </p>
                      )}
                      {product.external_url && (
                        <a
                          href={product.external_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center text-sm text-pink-700 hover:text-pink-800 font-medium transition group/link"
                          data-testid={`featured-brand-link-${product.id}`}
                        >
                          <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                          </svg>
                          <span className="border-b border-pink-700 group-hover/link:border-pink-800">
                            Buy on {product.brand_name || 'brand'} site
                          </span>
                          <svg className="w-3 h-3 ml-1 group-hover/link:translate-x-0.5 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </a>
                      )}
                    </div>
                  )}
                  
                  <div className="flex items-center justify-between">
                    <span className="text-2xl font-bold text-purple-600">
                      {formatINR(product.price)}
                    </span>
                    {product.category === 'featured-brands' ? (
                      <span className="text-xs text-gray-500 italic">External listing</span>
                    ) : (
                      <button
                        onClick={() => addToCart(product.id)}
                        className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition flex items-center space-x-2"
                        data-testid={`add-to-cart-${product.id}`}
                      >
                        <ShoppingCart className="w-4 h-4" />
                        <span>Add</span>
                      </button>
                    )}
                  </div>
                  {product.category !== 'featured-brands' && product.stock < 10 && product.stock > 0 && (
                    <p className="text-orange-600 text-sm mt-2">Only {product.stock} left!</p>
                  )}
                  {product.category !== 'featured-brands' && product.stock === 0 && (
                    <p className="text-red-600 text-sm mt-2">Out of stock</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Products;